/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, filter, first, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { CookieService } from 'ngx-cookie-service';

import { Action, NgRxState } from '@xbaht/common';
import { AuthenticationFetchService } from '@xbaht/common/fetches';

import { SessionExpiredService } from '../services/session-expired.service';
import { CookieStorage } from '../constants/storage';

@Injectable({
  providedIn: 'root',
})
export class ErrorFetchService implements HttpInterceptor {
  constructor(
    private ngrxStore: Store<NgRxState>,
    private cookieService: CookieService,
    private authenticationFetchService: AuthenticationFetchService,
    private sessionExpiredService: SessionExpiredService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // Loop guard: the refresh request itself failed — session fully expired
          if (req.url.includes('authentication/token')) {
            this.sessionExpiredService.isRefreshing$.next(false);
            this.sessionExpiredService.trigger();
            return throwError(() => err);
          }

          if (!this.sessionExpiredService.isRefreshing$.value) {
            return this.handleRefresh(req, next, err);
          }

          return this.waitForRefresh(req, next, err);
        }

        return throwError(() => err);
      })
    );
  }

  private handleRefresh(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalErr: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    this.sessionExpiredService.isRefreshing$.next(true);

    return this.authenticationFetchService.token().pipe(
      switchMap((response) => {
        this.sessionExpiredService.isRefreshing$.next(false);

        if (response.status && response.payload?.token) {
          const newToken = response.payload.token;

          this.ngrxStore.dispatch(
            Action.AuthAction.SetToken({ token: newToken })
          );
          this.cookieService.set(CookieStorage.token, newToken, {
            domain: location.hostname,
            path: '/',
            expires: 1,
            secure: true,
            sameSite: 'None',
          });

          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
          });
          return next.handle(retryReq);
        }

        this.sessionExpiredService.trigger();
        return throwError(() => originalErr);
      }),
      catchError(() => {
        this.sessionExpiredService.isRefreshing$.next(false);
        this.sessionExpiredService.trigger();
        return throwError(() => originalErr);
      })
    );
  }

  private waitForRefresh(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalErr: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return this.sessionExpiredService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      first(),
      switchMap(() =>
        this.ngrxStore.select('Auth').pipe(
          first(),
          switchMap((auth) => {
            if (auth.token) {
              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${auth.token}` },
              });
              return next.handle(retryReq);
            }
            return throwError(() => originalErr);
          })
        )
      )
    );
  }
}

export const ErrorFetchProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ErrorFetchService,
  multi: true,
};
